/**
 * Intentionally curated rather than comprehensive. Each entry points only to
 * an authority-controlled route and records the primary page used to verify it.
 */
export const CLAIM_SUBMISSION_ROUTES = [
    {
        authorityName: 'GOV.UK council finder',
        areaLabel: 'England and Wales',
        nation: 'England and Wales',
        roadScope: 'Starting point for identifying the local council responsible for a road',
        routeKind: 'guidance-page',
        officialUrl: 'https://www.gov.uk/claim-for-damage-to-your-vehicle',
        sourceUrl: 'https://www.gov.uk/claim-for-damage-to-your-vehicle',
        verifiedDate: '2026-07-19',
        scopeNote: 'Use this starting point when the maintaining authority is unclear; it is not itself a council claim form.',
        submissionNote: 'Find the responsible council, then follow the current compensation instructions on that council’s website.',
    },
    {
        authorityName: 'National Highways',
        areaLabel: 'England',
        nation: 'England',
        roadScope: 'Motorways and strategic trunk roads managed by National Highways',
        routeKind: 'online-form',
        officialUrl: 'https://nationalhighways.co.uk/our-roads/network-claims/claims-against-national-highways-red-claims/trunk-road-and-motorway-third-party-damage-report-form-red-claims/',
        sourceUrl: 'https://nationalhighways.co.uk/our-roads/network-claims/claims-against-national-highways-red-claims/',
        verifiedDate: '2026-07-19',
        scopeNote: 'Use only after confirming that the incident road is part of the English strategic road network.',
        submissionNote: 'Contact the Customer Contact Centre first for the reference number required by the Red Claim form.',
    },
    {
        authorityName: 'Transport for London',
        areaLabel: 'London',
        nation: 'England',
        roadScope: 'TfL-managed red routes',
        routeKind: 'contact-route',
        officialUrl: 'https://tfl.gov.uk/help-and-contact/contact-us-about-streets-and-other-road-issues',
        sourceUrl: 'https://tfl.gov.uk/help-and-contact/contact-us-about-streets-and-other-road-issues',
        verifiedDate: '2026-07-19',
        scopeNote: 'Check that TfL, rather than a London borough, manages the road before using this contact route.',
        submissionNote: 'Use the streets and road-issues contact route and clearly state that you want to make a damage claim.',
    },
    {
        authorityName: 'Derbyshire County Council',
        areaLabel: 'Derbyshire',
        nation: 'England',
        roadScope: 'Roads, pavements and public footpaths maintained by Derbyshire County Council',
        routeKind: 'online-form',
        officialUrl: 'https://www.derbyshire.gov.uk/transport-roads/roads-traffic/claims/personal-injury-or-damage-to-property.aspx',
        sourceUrl: 'https://www.derbyshire.gov.uk/transport-roads/roads-traffic/claims/personal-injury-or-damage-to-property.aspx',
        verifiedDate: '2026-07-19',
        scopeNote: 'This route does not cover roads maintained by National Highways or another local authority.',
        submissionNote: 'Read the evidence requirements on the council page before opening its compensation application form.',
    },
    {
        authorityName: 'Dudley Metropolitan Borough Council',
        areaLabel: 'Dudley',
        nation: 'England',
        roadScope: 'Dudley Council services and maintained local roads',
        routeKind: 'guidance-page',
        officialUrl: 'https://www.dudley.gov.uk/online-payments-enquiries/risk-management-insurance/',
        sourceUrl: 'https://www.dudley.gov.uk/online-payments-enquiries/risk-management-insurance/',
        verifiedDate: '2026-09-07',
        scopeNote: 'Confirm Dudley Council maintained the incident road; reporting a defect is a separate maintenance action.',
        submissionNote: 'Follow the council liability-claim instructions and provide incident details and why you allege responsibility.',
    },
    {
        authorityName: 'Bristol City Council',
        areaLabel: 'Bristol',
        nation: 'England',
        roadScope: 'Publicly maintained roads and pavements managed by Bristol City Council',
        routeKind: 'online-form',
        officialUrl: 'https://www.bristol.gov.uk/residents/streets-travel/report-a-street-issue/make-a-highways-claim-for-injury-or-damage-to-property',
        sourceUrl: 'https://www.bristol.gov.uk/residents/streets-travel/report-a-street-issue/make-a-highways-claim-for-injury-or-damage-to-property',
        verifiedDate: '2026-09-07',
        scopeNote: 'Motorways and the National Highways section of the A4 at Avonmouth use a different claim route.',
        submissionNote: 'Use the Highways Claim Form linked from this page if you are submitting without legal representation. Include any road-fault enquiry number.',
    },
    {
        authorityName: 'West Northamptonshire Council',
        areaLabel: 'West Northamptonshire',
        nation: 'England',
        roadScope: 'Local highways maintained by West Northamptonshire Council',
        routeKind: 'contact-route',
        officialUrl: 'https://www.westnorthants.gov.uk/highway-maintenance/damage-and-personal-injury-claims',
        sourceUrl: 'https://www.westnorthants.gov.uk/highway-maintenance/damage-and-personal-injury-claims',
        verifiedDate: '2026-09-07',
        scopeNote: 'West Northamptonshire is separate from North Northamptonshire. Motorways and trunk roads managed by National Highways use its claim route.',
        submissionNote: 'Provide the incident location when requesting a Public Liability Claim Form through the contact on this page. Return the form with photographs and receipts using the council instructions.',
    },
    {
        authorityName: 'Suffolk County Council',
        areaLabel: 'Suffolk',
        nation: 'England',
        roadScope: 'Highway network maintained by Suffolk County Council',
        routeKind: 'online-form',
        officialUrl: 'https://www.suffolk.gov.uk/roads-and-transport/highway-maintenance/making-a-highways-insurance-claim',
        sourceUrl: 'https://www.suffolk.gov.uk/roads-and-transport/highway-maintenance/making-a-highways-insurance-claim',
        verifiedDate: '2026-07-19',
        scopeNote: 'Use for a highway defect on Suffolk County Council’s network after checking the page requirements.',
        submissionNote: 'Open the council claim route from this guidance page and provide the evidence requested for your incident.',
    },
    {
        authorityName: 'Slough Borough Council',
        areaLabel: 'Slough',
        nation: 'England',
        roadScope: 'Services and local roads for which Slough Borough Council may be legally responsible',
        routeKind: 'online-form',
        officialUrl: 'https://www.slough.gov.uk/complaints-feedback/insurance-claims',
        sourceUrl: 'https://www.slough.gov.uk/complaints-feedback/insurance-claims',
        verifiedDate: '2026-07-19',
        scopeNote: 'The page offers an online claim form and a PDF process; confirm road responsibility before submitting.',
        submissionNote: 'Choose the official online insurance claim form or downloadable claim form linked from the council page.',
    },
    {
        authorityName: 'Caerphilly County Borough Council',
        areaLabel: 'Caerphilly County Borough',
        nation: 'Wales',
        roadScope: 'Highways and footways maintained by Caerphilly County Borough Council',
        routeKind: 'downloadable-form',
        officialUrl: 'https://www.caerphilly.gov.uk/My-Council/Complaints-and-feedback/Insurance-claims/Highways-insurance-claims.aspx',
        sourceUrl: 'https://www.caerphilly.gov.uk/My-Council/Complaints-and-feedback/Insurance-claims/Highways-insurance-claims.aspx',
        verifiedDate: '2026-07-19',
        scopeNote: 'This is a Welsh local-authority route, not the route for a Welsh Government motorway or trunk road.',
        submissionNote: 'Report the defect, retain the enquiry number, then download and submit the council insurance claim form.',
    },
    {
        authorityName: 'Traffic Wales (Welsh Government strategic road network)',
        areaLabel: 'Wales strategic road network',
        nation: 'Wales',
        roadScope: 'Motorways and trunk roads for which the Welsh Ministers are highway authority',
        routeKind: 'online-form',
        officialUrl: 'https://traffic.wales/report-issue',
        sourceUrl: 'https://traffic.wales/claims',
        verifiedDate: '2026-07-19',
        scopeNote: 'Traffic Wales provides this route for Welsh motorways and trunk roads; use the relevant council for other Welsh local roads.',
        submissionNote: 'Select “Claims” in the non-urgent report form and provide the incident details; the carriageway-defect category is for reporting a road fault.',
    },
];
